import React from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "./ThemedText";
import { ThemedToolTip } from "./ThemedToolTip";
import { BrandColors } from "@/constants/ThemeColours";

/**
 * Definition of an individual batch action item.
 */
export interface IBatchAction {
  id: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: (e?: any) => void;
  destructive?: boolean;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "destructive";
  testID?: string;
}

/**
 * Props for the ThemedActionHoverPanel component.
 */
export interface IThemedActionHoverPanelProps {
  /** Whether the floating action panel is visible */
  visible: boolean;
  /** Number of currently selected items */
  selectedCount: number;
  /** Total count of items available in the list */
  totalCount?: number;
  /** Callback to select all items */
  onSelectAll?: () => void;
  /** Callback to clear selection and dismiss menu */
  onClearSelection: () => void;
  /** Actions available for the selected batch */
  actions: IBatchAction[];
  /** Optional custom container style overrides */
  containerStyle?: StyleProp<ViewStyle>;
  /** Whether the panel floats at the screen bottom (default: true) */
  floating?: boolean;
}

/**
 * Floating hover-over action panel displayed when one or more items are selected in a list.
 * Styled in signature Archival Blue Linen (`BrandColors.blueLinen.hex`) with high-contrast Obsidian Ink accents.
 * Conforms to Memories UX Standards (token colors, anti-AI palette, ThemedToolTip integration).
 */
export function ThemedActionHoverPanel({
  visible,
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  actions,
  containerStyle,
  floating = true,
}: IThemedActionHoverPanelProps) {
  const { width: windowWidth } = useWindowDimensions();

  if (!visible || selectedCount <= 0) {
    return null;
  }

  const isNarrow = windowWidth < 600;

  return (
    <View
      style={[
        floating ? styles.floatingContainer : styles.staticContainer,
        containerStyle,
      ]}
      pointerEvents="box-none"
    >
      <View style={styles.menuCard}>
        {/* Left Side: Selection Count & Clear/Select All */}
        <View style={styles.leftSection}>
          <View style={styles.countBadge}>
            <ThemedText style={styles.countText}>{selectedCount}</ThemedText>
          </View>
          <ThemedText style={styles.selectedLabel} numberOfLines={1}>
            {selectedCount === 1 ? "selected" : "selected"}
          </ThemedText>

          {onSelectAll && totalCount !== undefined && totalCount > selectedCount && (
            <ThemedToolTip text={`Select all ${totalCount} items`}>
              <TouchableOpacity
                onPress={onSelectAll}
                style={styles.miniBtn}
                activeOpacity={0.7}
                accessibilityLabel={`Select all ${totalCount} items`}
              >
                <ThemedText style={styles.miniBtnText}>
                  All
                </ThemedText>
              </TouchableOpacity>
            </ThemedToolTip>
          )}

          <ThemedToolTip text="Clear selection">
            <TouchableOpacity
              onPress={onClearSelection}
              style={styles.closeBtn}
              activeOpacity={0.7}
              accessibilityLabel="Clear selection"
            >
              <Ionicons name="close" size={14} color="#161F36" />
            </TouchableOpacity>
          </ThemedToolTip>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Right Side: Action Buttons */}
        <View style={styles.actionsSection}>
          {actions.map((act) => {
            const isDestructive = act.destructive;
            const actionColor = isDestructive ? BrandColors.beetRed.hex : "#161F36";

            return (
              <ThemedToolTip key={act.id} text={act.label}>
                <TouchableOpacity
                  onPress={act.onPress}
                  disabled={act.disabled}
                  style={[
                    styles.actionBtn,
                    isDestructive && styles.destructiveBtn,
                    act.disabled && styles.disabledBtn,
                  ]}
                  activeOpacity={0.7}
                  accessibilityLabel={act.label}
                  testID={act.testID}
                >
                  <Ionicons
                    name={act.icon}
                    size={16}
                    color={actionColor}
                    style={!isNarrow ? { marginRight: 4 } : undefined}
                  />
                  {!isNarrow && (
                    <ThemedText
                      style={[
                        styles.actionLabel,
                        { color: actionColor },
                        isDestructive && styles.destructiveText,
                      ]}
                      numberOfLines={1}
                    >
                      {act.label}
                    </ThemedText>
                  )}
                </TouchableOpacity>
              </ThemedToolTip>
            );
          })}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  floatingContainer: {
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 9999,
    paddingHorizontal: 16,
  },
  staticContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
  },
  menuCard: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: "rgba(28, 35, 51, 0.25)",
    backgroundColor: BrandColors.blueLinen.hex,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
    maxWidth: "96%",
    flexWrap: "wrap",
    gap: 8,
  },
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  countBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    minWidth: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#161F36",
  },
  countText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "700",
  },
  selectedLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#161F36",
  },
  miniBtn: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(28, 35, 51, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 0.45)",
  },
  miniBtnText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#161F36",
  },
  closeBtn: {
    padding: 3,
    borderRadius: 4,
    backgroundColor: "rgba(28, 35, 51, 0.12)",
    alignItems: "center",
    justifyContent: "center",
  },
  divider: {
    width: 1,
    height: 24,
    marginHorizontal: 4,
    backgroundColor: "rgba(28, 35, 51, 0.2)",
  },
  actionsSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  actionBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(28, 35, 51, 0.18)",
    backgroundColor: "rgba(255, 255, 255, 0.45)",
  },
  actionLabel: {
    fontSize: 11.5,
    fontWeight: "600",
    color: "#161F36",
  },
  destructiveBtn: {
    backgroundColor: "rgba(171, 23, 23, 0.12)",
    borderColor: "rgba(171, 23, 23, 0.3)",
  },
  destructiveText: {
    color: BrandColors.beetRed.hex,
  },
  disabledBtn: {
    opacity: 0.4,
  },
});
